'use client';
import { FC, useState } from 'react';
import { Box, Typography, Paper, Stack, TextField, Autocomplete } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { DebouncedTextField } from '@/components/common/DebouncedTextField';
import { useBackend } from '@/hooks/useBackend';
import { formatDate } from '@/lib/utils/dateUtils';
import { Footer } from '@/components/details/Footer';
import DetailField from '@/components/common/DetailField';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { PrivilegeEnum, CommunicationDto, StatusDto } from '@/api/ApiClient';
import { COMMUNICATION_FORMAT_OPTIONS } from '@/lib/types/communicationTypes';
import { nullIfBlank } from '@/lib/utils/nullIfBlank';

import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useCommunicationData, useIsDataModified } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createCommunicationSaveValidation } from '@/lib/validation/saveValidationBuilders';
import { toUpdateCommunicationRequest } from '@/lib/entity/communicationRequests';

export const selectOptions: OptionKey[] = ['researcherOptions', 'facultyOptions'];

const CommunicationDetails: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();

  const { setDataModified, updateDataPartial, updateData } = useTree();
  const { selectedNode } = useTreeSelection();

  const [isManuallyEditing, setIsManuallyEditing] = useState(false);

  const communication = useCommunicationData(selectedNode);

  const [formatError, setFormatError] = useState(false);
  const [titleError, setTitleError] = useState(false);

  const privilege = PrivilegeEnum.Communication;

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  const getRealUserName = (userId: string) =>
    options.researcherOptions?.find((k) => k.key === userId)?.value ||
    options.facultyOptions?.find((k) => k.key === userId)?.value ||
    userId;

  const convertDateForBackend = (date: Date | null): Date | undefined => {
    return date ? date : undefined;
  };

  const handleModification = (modified: Partial<CommunicationDto>) => {
    if (!selectedNode) return;
    updateDataPartial(selectedNode.dataId, modified);

    // Clear validation errors when user starts typing
    if (modified.format !== undefined) {
      setFormatError(false);
    }
    if (modified.title !== undefined) {
      setTitleError(false);
    }
  };

  // Validation function
  const isValid = () => {
    if (!communication) return false;
    return validateRequiredToSave(createCommunicationSaveValidation(communication)).isValid;
  };

  const handleSave = async () => {
    if (!communication?.id || !selectedNode) return;

    const saveValidation = validateRequiredToSave(createCommunicationSaveValidation(communication));
    setFormatError(!!saveValidation.fieldErrors.format);
    setTitleError(!!saveValidation.fieldErrors.title);

    if (!saveValidation.isValid) {
      showSnackbar('Please fill out all required fields', 'error');
      return;
    }

    try {
      const updatedData = await backend.communication.update(
        communication.id,
        toUpdateCommunicationRequest(communication)
      );
      updateData(selectedNode.dataId, updatedData);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after save
      showSnackbar('Communication saved successfully', 'success');
    } catch (error) {
      log.error('[CommunicationDetails] Failed to save communication:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save communication.', 'error');
    }
  };

  if (!options || optionsLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading options...
        </Typography>
      </Box>
    );
  }

  if (!communication || selectedNode?.type !== 'communication') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {!communication ? 'Select a node to view details' : 'Selected node is not a communication.'}
        </Typography>
      </Box>
    );
  }

  const isSubmitted = communication.status === StatusDto.Submitted;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={3}>
          {/* Top Container - Metadata */}
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={3}>
              {/* Row 1: Created by and Date side by side */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Created by"
                  value={getRealUserName(communication.createdByUser || '')}
                  isReadOnly={true}
                  width={{ width: '48%' }}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DetailField
                    label="Date"
                    value={communication.date ? formatDate(communication.date) : 'N/A'}
                    isReadOnly={isReadOnly || isSubmitted}
                    width={{ width: '48%' }}
                  >
                    <DatePicker
                      value={communication.date ? new Date(communication.date) : null}
                      onChange={(newValue) => {
                        handleModification({
                          date: convertDateForBackend(newValue),
                        });
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                        },
                      }}
                    />
                  </DetailField>
                </LocalizationProvider>
              </Box>

              {/* Row 2: Format and Participants side by side */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <DetailField
                  label="Participants"
                  value={communication.participants || 'N/A'}
                  isReadOnly={isReadOnly || isSubmitted}
                  width={{ width: '48%' }}
                >
                  <DebouncedTextField
                    value={communication.participants || ''}
                    onUpdate={(value) => handleModification({ participants: value })}
                    fullWidth
                    size="small"
                  />
                </DetailField>
                <DetailField
                  label="Format"
                  value={communication.format || 'N/A'}
                  isReadOnly={isReadOnly || isSubmitted}
                  width={{ width: '48%' }}
                >
                  <Autocomplete
                    options={COMMUNICATION_FORMAT_OPTIONS}
                    getOptionLabel={(option) => option.value}
                    value={COMMUNICATION_FORMAT_OPTIONS.find((opt) => opt.key === communication.format) || null}
                    onChange={(_, newValue) => handleModification({ format: nullIfBlank(newValue?.key) })}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        error={formatError}
                        helperText={formatError ? 'Format is required' : ''}
                      />
                    )}
                    fullWidth
                  />
                </DetailField>
              </Box>

              {/* Row 3: Title full width */}
              <DetailField
                label="Title"
                value={communication.title || 'N/A'}
                isReadOnly={isReadOnly || isSubmitted}
                width={{ width: '100%' }}
              >
                <DebouncedTextField
                  value={communication.title || ''}
                  onUpdate={(value) => handleModification({ title: value })}
                  fullWidth
                  size="small"
                  error={titleError}
                  helperText={titleError ? 'Title is required' : ''}
                />
              </DetailField>
            </Stack>
          </Paper>

          {/* Content Container */}
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <DetailField
              label="Content"
              value={<span dangerouslySetInnerHTML={{ __html: communication.text || '' }} />}
              isReadOnly={isReadOnly || isSubmitted}
              width={{ width: '100%' }}
            >
              <RichTextEditor
                value={communication.text || ''}
                onChange={(content) => {
                  handleModification({ text: content });
                }}
              />
            </DetailField>
          </Paper>
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <Footer
          lastChangedDate={communication.lastChangedDate}
          lastChangedBy={getRealUserName(communication.lastChangedByUser || '')}
          status={communication.status}
          isReadOnly={isReadOnly}
          setIsReadOnly={(val) => setIsManuallyEditing(!val)}
          onSave={handleSave}
          privilege={privilege}
          isFormValid={isValid()}
        />
      </Box>
    </Box>
  );
};

export default CommunicationDetails;
